// Sentry / GlitchTip wiring for the React app.
//
// Initialised once from src/index.js before the React tree mounts. The same
// `Sentry` namespace is re-exported so other modules (store enhancer, axios
// interceptor, ErrorBoundary) read the configured hub through a single import
// path and we don't accidentally end up with two SDK instances.
//
// Configuration is build-time only (CRA inlines REACT_APP_* into the bundle).
// Leave REACT_APP_SENTRY_DSN unset to disable capture entirely — useful for
// local dev so we don't spam the GlitchTip project.

import * as Sentry from '@sentry/react';

const DSN = process.env.REACT_APP_SENTRY_DSN || '';
const ENV = process.env.REACT_APP_ENVIRONMENT || 'production';

// Route events through our own origin so ad-blockers / tracker filters don't
// drop them. The Django view at /api/v1/sentry-tunnel/ accepts the raw
// envelope and forwards to the real GlitchTip in a background thread.
// Same-origin, so a relative path works in every environment.
const TUNNEL = '/api/v1/sentry-tunnel/';

// Gate: only send events from a production build. `npm run start` sets
// NODE_ENV=development; `npm run build` sets NODE_ENV=production. To send
// from dev temporarily (verification), set REACT_APP_FORCE_SENTRY=true
// in .env.development.local and restart the dev server.
const FORCE = process.env.REACT_APP_FORCE_SENTRY === 'true';
const IS_PROD_BUILD = process.env.NODE_ENV === 'production';

let initialised = false;

// Strip the query string from any URL before it leaves the browser. App URLs
// can carry secrets in the query (e.g. /user/verify/?uid=...&token=...), and
// Sentry's default fetch/xhr breadcrumbs *and* our axios interceptor both
// record request URLs. Keep the path (useful for triage), drop the whole query
// — the risky part. See audit A260520-FE-M1.
export function scrubUrl(value) {
  if (typeof value !== 'string') return value;
  const q = value.indexOf('?');
  return q === -1 ? value : value.slice(0, q);
}

function scrubBreadcrumb(breadcrumb) {
  if (breadcrumb?.data?.url) {
    breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
  }
  return breadcrumb;
}

function scrubEvent(event) {
  if (event.request?.url) event.request.url = scrubUrl(event.request.url);
  if (event.request?.query_string) delete event.request.query_string;
  if (event.extra?.url) event.extra.url = scrubUrl(event.extra.url);
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs.forEach(scrubBreadcrumb);
  return event;
}

export function initSentry() {
  if (initialised || !DSN || (!IS_PROD_BUILD && !FORCE)) return;
  Sentry.init({
    dsn: DSN,
    tunnel: TUNNEL,
    environment: ENV,
    // GlitchTip's performance / tracing support is limited; keep it off.
    tracesSampleRate: 0.0,
    // Default integrations cover GlobalHandlers (uncaught + unhandledrejection)
    // and Breadcrumbs (console, fetch, history, ui clicks). That's what we want.
    sendDefaultPii: false,
    // Redact secrets carried in URL query strings before any event/breadcrumb
    // leaves the browser (see scrubUrl). A260520-FE-M1.
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubEvent,
    // Drop noisy events that aren't actionable.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Network Error', // pure offline; axios interceptor will re-capture real 5xx
      // Masked cross-origin errors, e.g. Firefox iOS's injected content
      // scripts throwing on focus events. No stack, no message, no action.
      'Script error.',
    ],
    release: process.env.REACT_APP_RELEASE || undefined,
  });
  initialised = true;
}

export { Sentry };
